import Table from '@eduzz/houston-ui/Table';
import Typography from '@eduzz/houston-ui/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import MyIcon from 'components/Icon';
import { enums } from 'helpers';
import { PRODUCT_STATUS_LABEL } from 'helpers/enums';
import React, { memo, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { TableActionButton } from 'v2/components/Shared/TableActionButton';
import Truncate from 'v2/components/Shared/TruncateText';

import type { TableSort } from '@eduzz/houston-ui/Table/interface';

import { useStyles } from './styles';
import DataChildren from '../DataChildren';
import ShowPrice from '../ShowPrice';

enum AppCodes {
  BLINKET = 28,
  ALUMY = 36,
}

export type PaginationType = {
  current_page: number;
  per_page: number;
  optionsPerPage: string;
  onChangePage: any;
  onChangePerPage: any;
  total: number;
};

type Props = {
  products: any[];
  listProductsChildren?: any;
  order: string;
  coproduction?: boolean;
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
  onClickOptions?: (product: any, activatedChildren: any) => void;
  isLoading: boolean;
  pagination: PaginationType;
};

const ShowApp = ({ product }: any) => {
  const apps = {
    blinket: {
      logo: '/img/blinket-symbol.svg',
      logoWidth: 20,
      description: 'Lá você vai encontrar tudo para criar e gerenciar seus eventos.',
      whyLink: '',
      whyLabel: 'a Blinket',
      color: '#ED1A62',
      tooltip: 'Esse produto foi cadastrado na plataforma Blinket',
      alt: 'Blinket',
    },
    alumy: {
      logo: '/img/alumy-symbol.svg',
      logoWidth: 20,
      description: 'Lá você vai encontrar tudo para criar e gerenciar seus clubes de assinatura.',
      whyLink: '',
      whyLabel: 'a Alumy',
      color: '#ED1A62',
      tooltip: 'Esse produto foi cadastrado na plataforma Alumy',
      alt: 'Alumy',
    },
  };

  const verifyApp = useCallback(() => {
    if (product.cnt_partner_cod === AppCodes.BLINKET) return apps.blinket;
    if (product.cnt_partner_cod === AppCodes.ALUMY) return apps.alumy;
    return null;
  }, [apps.alumy, apps.blinket, product]);

  const currentApp = verifyApp();

  return (
    <div>
      {currentApp && (
        <Tooltip title={currentApp.tooltip}>
          <img width={currentApp.logoWidth} src={currentApp.logo} alt={currentApp.alt} style={{ marginLeft: '0.5rem' }} />
        </Tooltip>
      )}
    </div>
  );
};

const ProductTable = ({ products, order, listProductsChildren, coproduction, onSort, onClickOptions, isLoading, pagination }: Props) => {
  const classes = useStyles();

  const [field, direction] = order.split('-');

  const [sort, setSort] = useState<TableSort>({ field, direction } as TableSort);

  const history = useHistory();

  const onSortTable = useCallback(
    (sortField: string, sortDirection: 'asc' | 'desc') => {
      setSort({
        field: sortField,
        direction: sortDirection,
      });
      onSort(sortField, sortDirection);
    },
    [onSort]
  );

  const onDoubleClick = (id: string) => {
    if (coproduction) return;
    history.push(`/producer/product/editproduct/${id}`);
  };

  return (
    <Table loading={isLoading} total={pagination?.total} className={classes.root}>
      <Table.Header sortDirection={sort.direction as 'asc' | 'desc'} sortField={sort.field} onSort={onSortTable}>
        {!coproduction && (
          <>
            <Table.Column sortableField='title' width={800}>
              Produto
            </Table.Column>
            <Table.Column align='center'>Status</Table.Column>
            <Table.Column sortableField='price' align='left'>
              Preço
            </Table.Column>
            <Table.Column></Table.Column>
          </>
        )}
        {coproduction && (
          <>
            <Table.Column>Produto</Table.Column>
            <Table.Column align='center'>Status</Table.Column>
            <Table.Column align='left'>Preço</Table.Column>
            <Table.Column align='left'>Comissão</Table.Column>
            <Table.Column align='left'>Comissão recorrente</Table.Column>
            <Table.Column></Table.Column>
          </>
        )}
      </Table.Header>
      <Table.Body>
        {(products || []).map((row, index) => (
          <Table.Row data={row} index={index} key={row.id} onDoubleClick={() => onDoubleClick(row.id)}>
            <Table.Cell align='left' className='product__cell'>
              <div className='image' style={{ backgroundImage: `url(${row.image})` }}></div>
              <div>
                <div title={row.title || 'Desconhecido'}>
                  <Typography size='xxs' weight='bold' className={classes.productTitle}>
                    <Truncate count={100} final='...'>
                      {row.title || 'Desconhecido'}
                    </Truncate>
                  </Typography>
                  <Typography size='xxs'>{row.id}</Typography>
                </div>

                {!row.has_children && (
                  <div className={`moderation__status ${enums.PRODUCT_STATUS_CLASS[row.moderation_status ? row.moderation_status : '']}`}>
                    {enums.PRODUCT_STATUS[row.moderation_status]}

                    {row.charge_type === 'A' && (
                      <Tooltip title='Recorrência' placement='top'>
                        <div className='charger-type'>
                          <MyIcon className='icon' name='icon-assinaturas' />
                        </div>
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
              <ShowApp product={row} />
            </Table.Cell>
            <Table.Cell className={row.status === 'active' ? '--active' : '--inactive'} align='center'>
              {PRODUCT_STATUS_LABEL[row.status]}
            </Table.Cell>
            <Table.Cell align='left'>
              <ShowPrice product={row} commission={coproduction ? 'coproduction' : 'normal'} />
            </Table.Cell>
            {coproduction && (
              <>
                <Table.Cell align='left'>
                  <ShowPrice product={row} commission='percentage' />
                </Table.Cell>
                <Table.Cell align='left'>
                  <ShowPrice product={row} commission='subscription' />
                </Table.Cell>
              </>
            )}
            <Table.Cell align='right' id={`product-see-details-${index}`}>
              <TableActionButton onClick={() => onClickOptions(row, row.has_children)} disabled={!!listProductsChildren?.isFetching} />
            </Table.Cell>
            {row.has_children && <DataChildren product={row} onClickOptions={onClickOptions} />}
          </Table.Row>
        ))}
      </Table.Body>
      {!isLoading && pagination && (
        <Table.Pagination
          page={pagination?.current_page}
          perPage={pagination?.per_page}
          optionsPerPage={pagination?.optionsPerPage}
          onChangePage={pagination.onChangePage}
          onChangePerPage={pagination.onChangePerPage}
        />
      )}
    </Table>
  );
};

export default memo(ProductTable);
